import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import {
  Badge,
  Card,
  ConfirmDialog,
  Empty,
  Failure,
  Field,
  Loading,
  Notice,
} from '../components/ui.js';
import { formatDateTime, formatRelative } from '../lib/format.js';

interface IncidentSummary {
  id: string;
  kind: string;
  state: string;
  source: string;
  reason: string;
  opened_at: string;
  cleared_at: string | null;
  clear_reason: string | null;
  schools: Array<{ id: string; name: string }>;
  device_count: number;
  confirmed_count: number;
  delivery_states: Record<string, number>;
}

interface IncidentDetail extends IncidentSummary {
  deliveries: Array<{
    device_id: string;
    device_name: string;
    school_name: string;
    state: string;
    code: string | null;
    detail: string | null;
    confirmed_at: string | null;
    cleared_at: string | null;
    received: boolean;
  }>;
}

const DELIVERY_LABELS: Record<string, { label: string; tone: string; icon: string }> = {
  PENDING: { label: 'لم يُؤكَّد الاستلام', tone: 'muted', icon: '…' },
  ACCEPTED: { label: 'استلم الأمر', tone: 'info', icon: '→' },
  ACTIVE: { label: 'بدأ الإنذار', tone: 'danger', icon: '⚠' },
  CLEARED: { label: 'أُلغي', tone: 'ok', icon: '✔' },
  REJECTED: { label: 'مرفوض', tone: 'warn', icon: '✖' },
  UNREACHABLE: { label: 'غير قابل للوصول', tone: 'warn', icon: '▲' },
};

/**
 * Emergency console (§7 page 11, §15).
 *
 * Deliberately a separate screen with its own permission. Activation and
 * cancellation are independent grants, and nothing here reports a device as
 * having received the alarm unless that device acknowledged it.
 */
export function EmergencyPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showActivate, setShowActivate] = useState(false);
  const [clearing, setClearing] = useState<IncidentSummary | null>(null);
  const [clearReason, setClearReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const incidents = useQuery({
    queryKey: ['incidents'],
    queryFn: () => get<{ items: IncidentSummary[]; total: number }>('/incidents?page_size=50'),
    refetchInterval: 5000,
  });

  const detail = useQuery({
    queryKey: ['incident', selected],
    queryFn: () => get<IncidentDetail>(`/incidents/${selected}`),
    enabled: Boolean(selected),
    refetchInterval: 5000,
  });

  const schools = useQuery({
    queryKey: ['schools', 'all'],
    queryFn: () => get<{ items: Array<{ id: string; name: string }> }>('/schools?page_size=500'),
  });

  const activate = useMutation({
    mutationFn: (body: { kind: string; school_ids: string[]; reason: string }) =>
      post<{ incident_id: string; device_count: number; offline_device_count: number; note: string }>(
        '/incidents',
        body,
      ),
    onSuccess: (res) => {
      setShowActivate(false);
      setError(null);
      setSelected(res.incident_id);
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const clear = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      post(`/incidents/${vars.id}/clear`, { reason: vars.reason }),
    onSuccess: () => {
      setClearing(null);
      setClearReason('');
      void queryClient.invalidateQueries({ queryKey: ['incidents'] });
      void queryClient.invalidateQueries({ queryKey: ['incident', selected] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const active = incidents.data?.items.filter((i) => i.state === 'ACTIVE') ?? [];
  const history = incidents.data?.items.filter((i) => i.state !== 'ACTIVE') ?? [];

  return (
    <div className="emergency-page">
      <div className="row between" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: 'var(--danger)' }}>⚠ الطوارئ والإخلاء</h2>
        {can('incident.activate') ? (
          <button className="danger" onClick={() => setShowActivate(true)}>
            تفعيل إنذار
          </button>
        ) : null}
      </div>

      <Notice tone="warn">
        هذه طبقة إذاعة وتنبيه مساندة. ليست بديلًا معتمدًا عن منظومة إنذار المدرسة، ويجب أن يشمل
        القبول الميداني مسؤول المدرسة أو الصيانة ونوع الإنذار المعتمد لديهم.
      </Notice>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {!can('incident.activate') && !can('incident.clear') ? (
        <Notice tone="info">
          صلاحيات الطوارئ مستقلة عن الدور العام ومعطّلة افتراضيًا. لا تملك حاليًا صلاحية التفعيل أو
          الإلغاء؛ يمكنك الاطلاع فقط.
        </Notice>
      ) : null}

      <Card title={`الحوادث النشطة (${active.length})`}>
        {incidents.isLoading ? (
          <Loading />
        ) : incidents.error ? (
          <Failure error={incidents.error} onRetry={() => void incidents.refetch()} />
        ) : active.length === 0 ? (
          <Empty title="لا توجد حوادث نشطة" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>النوع</th>
                  <th>المدارس</th>
                  <th>المصدر</th>
                  <th>السبب</th>
                  <th>الأجهزة المؤكِّدة</th>
                  <th>منذ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((i) => (
                  <tr key={i.id}>
                    <td>
                      <Badge tone="danger" icon="⚠">
                        {i.kind === 'evacuation' ? 'إخلاء' : 'طوارئ عام'}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 13 }}>{i.schools.map((s) => s.name).join('، ')}</td>
                    <td className="mono" style={{ fontSize: 12.5 }}>
                      {i.source}
                    </td>
                    <td style={{ fontSize: 13 }}>{i.reason}</td>
                    <td>
                      <span className="num">{i.confirmed_count}</span> /{' '}
                      <span className="num">{i.device_count}</span>
                    </td>
                    <td style={{ fontSize: 12.5 }}>{formatRelative(i.opened_at)}</td>
                    <td className="actions">
                      <button onClick={() => setSelected(i.id)}>تفاصيل</button>
                      {can('incident.clear') ? (
                        <button className="danger" onClick={() => setClearing(i)}>
                          إلغاء الحادثة
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {active.length > 1 ? (
          <Notice tone="info">
            توجد أكثر من حادثة نشطة. إلغاء واحدة لا يلغي الأخريات، والجهاز يشغّل الأعلى أولوية.
          </Notice>
        ) : null}
      </Card>

      {selected && detail.data ? (
        <Card
          title="حالة استلام كل جهاز"
          subtitle={detail.data.kind === 'evacuation' ? 'إخلاء' : 'طوارئ عام'}
          actions={<button onClick={() => setSelected(null)}>إغلاق</button>}
        >
          <Notice tone="info">
            «بدأ الإنذار» تعني أن الجهاز أرسل تأكيدًا ببدء الإخراج الصوتي. الجهاز غير المتصل لا
            يُعرض على أنه تلقى الإنذار.
          </Notice>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الجهاز</th>
                  <th>المدرسة</th>
                  <th>حالة الاستلام</th>
                  <th>وقت التأكيد</th>
                  <th>ملاحظة</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.deliveries.map((d) => {
                  const st = DELIVERY_LABELS[d.state] ?? DELIVERY_LABELS.PENDING!;
                  return (
                    <tr key={d.device_id}>
                      <td>{d.device_name}</td>
                      <td>{d.school_name}</td>
                      <td>
                        <Badge tone={st.tone} icon={st.icon}>
                          {st.label}
                        </Badge>
                      </td>
                      <td style={{ fontSize: 12.5 }}>{formatDateTime(d.confirmed_at)}</td>
                      <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{d.detail ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <Card title="سجل الحوادث السابقة">
        {history.length === 0 ? (
          <Empty title="لا يوجد سجل بعد" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>النوع</th>
                  <th>المدارس</th>
                  <th>سبب التفعيل</th>
                  <th>وقت التفعيل</th>
                  <th>وقت الإلغاء</th>
                  <th>سبب الإلغاء</th>
                </tr>
              </thead>
              <tbody>
                {history.map((i) => (
                  <tr key={i.id}>
                    <td>{i.kind === 'evacuation' ? 'إخلاء' : 'طوارئ عام'}</td>
                    <td style={{ fontSize: 13 }}>{i.schools.map((s) => s.name).join('، ')}</td>
                    <td style={{ fontSize: 13 }}>{i.reason}</td>
                    <td style={{ fontSize: 12.5 }}>{formatDateTime(i.opened_at)}</td>
                    <td style={{ fontSize: 12.5 }}>{formatDateTime(i.cleared_at)}</td>
                    <td style={{ fontSize: 12.5 }}>{i.clear_reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showActivate ? (
        <ActivateDialog
          schools={schools.data?.items ?? []}
          busy={activate.isPending}
          onCancel={() => setShowActivate(false)}
          onConfirm={(body) => activate.mutate(body)}
        />
      ) : null}

      {clearing ? (
        <ConfirmDialog
          title="إلغاء الحادثة"
          danger
          busy={clear.isPending}
          confirmLabel="إلغاء هذه الحادثة"
          message={
            <>
              سيُرسل أمر إلغاء لهذه الحادثة فقط، إلى{' '}
              <strong className="num">{clearing.device_count}</strong> جهازًا. لن تتأثر أي حادثة
              أخرى نشطة، ولن تُستأنف الأصوات العادية التي قُطعت.
            </>
          }
          onCancel={() => setClearing(null)}
          onConfirm={() => clear.mutate({ id: clearing.id, reason: clearReason })}
        >
          <Field label="سبب الإلغاء (يُسجَّل في التدقيق)">
            <input value={clearReason} onChange={(e) => setClearReason(e.target.value)} required />
          </Field>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function ActivateDialog({
  schools,
  busy,
  onCancel,
  onConfirm,
}: {
  schools: Array<{ id: string; name: string }>;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (body: { kind: string; school_ids: string[]; reason: string }) => void;
}): JSX.Element {
  const [kind, setKind] = useState<'evacuation' | 'general'>('evacuation');
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <ConfirmDialog
      title="تفعيل إنذار"
      danger
      busy={busy}
      confirmLabel="تفعيل الإنذار الآن"
      message={
        <Notice tone="danger">
          سيُشغَّل الإنذار المعتمد فعليًا على سماعات المدارس المحددة، ويستمر حتى إلغاء معتمد. لا
          تستخدم هذه الشاشة للتجربة.
        </Notice>
      }
      onCancel={onCancel}
      onConfirm={() =>
        acknowledged && selected.length > 0 && reason.length >= 3
          ? onConfirm({ kind, school_ids: selected, reason })
          : undefined
      }
    >
      <Field label="نوع الإنذار">
        <select value={kind} onChange={(e) => setKind(e.target.value as 'evacuation' | 'general')}>
          <option value="evacuation">إخلاء (الأولوية القصوى)</option>
          <option value="general">طوارئ عام</option>
        </select>
      </Field>

      <Field label={`المدارس المستهدفة (${selected.length} محددة)`}>
        <div className="row" style={{ marginBottom: 6 }}>
          <button type="button" onClick={() => setSelected(schools.map((s) => s.id))}>
            تحديد الكل
          </button>
          <button type="button" onClick={() => setSelected([])}>
            إلغاء التحديد
          </button>
        </div>
        <div
          style={{
            maxHeight: 200,
            overflow: 'auto',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: 8,
            background: 'var(--surface)',
          }}
        >
          {schools.map((s) => (
            <label key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
              <input
                type="checkbox"
                checked={selected.includes(s.id)}
                onChange={(e) =>
                  setSelected((cur) =>
                    e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id),
                  )
                }
              />
              {s.name}
            </label>
          ))}
        </div>
      </Field>

      <Field label="سبب التفعيل (إلزامي، يُسجَّل في التدقيق)">
        <input value={reason} onChange={(e) => setReason(e.target.value)} minLength={3} required />
      </Field>

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text)' }}>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        أؤكد أن هذا تفعيل حقيقي وأتحمل مسؤوليته
      </label>
      {(!acknowledged || selected.length === 0 || reason.length < 3) && (
        <div className="error" style={{ marginTop: 6 }}>
          يلزم اختيار مدرسة واحدة على الأقل، وكتابة سبب، وتأكيد المسؤولية.
        </div>
      )}
    </ConfirmDialog>
  );
}
